import type { ReactNode } from "react";

import type { LaborOperatorDisplay } from "@/lib/labor/laborOperatorPresence";
import { cn } from "@/lib/utils";

function initialsForOperator(operator: LaborOperatorDisplay): string {
  if (operator.fullName) {
    const parts = operator.fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    }
    if (parts.length === 1) {
      const word = parts[0]!;
      if (word.length >= 2) return word.slice(0, 2).toUpperCase();
      if (word.length === 1) return (word + word).toUpperCase();
    }
  }
  const local = operator.email.split("@")[0] ?? operator.email;
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return (local + local).slice(0, 2).toUpperCase();
}

export function PrePackCartonOperatorStack(props: {
  operators: LaborOperatorDisplay[];
  viewerUserId?: string | null;
  /**
   * Carton workspace: hide the signed-in user so the stack only shows other people on the same carton.
   * Queue and overview cards should omit this flag so the viewer still sees themselves when clocked in.
   */
  omitViewer?: boolean;
  className?: string;
}): ReactNode {
  const { operators, viewerUserId, omitViewer, className } = props;
  const visibleOperators =
    omitViewer === true && viewerUserId !== undefined && viewerUserId !== null && viewerUserId !== ""
      ? operators.filter((operator) => operator.userId !== viewerUserId)
      : operators;
  if (visibleOperators.length === 0) {
    return null;
  }
  const names = visibleOperators.map((operator) => operator.fullName ?? operator.email).join(", ");
  return (
    <div
      className={cn("flex items-center", className)}
      role="group"
      aria-label={
        visibleOperators.length === 1
          ? `Clocked in: ${names}`
          : `Clocked in (${visibleOperators.length}): ${names}`
      }
    >
      {visibleOperators.map((operator, index) => {
        const isSelf = viewerUserId !== undefined && viewerUserId !== null && operator.userId === viewerUserId;
        const label = operator.fullName ?? operator.email;
        return (
          <div key={operator.userId} className={cn("relative", index > 0 && "-ml-2")} style={{ zIndex: index }}>
            {operator.avatarUrl ? (
              <img
                src={operator.avatarUrl}
                alt=""
                title={label}
                className={cn(
                  "h-8 w-8 rounded-full border-2 border-white object-cover shadow-sm ring-1 ring-slate-200",
                  isSelf && "ring-2 ring-brand-500 ring-offset-1",
                )}
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span
                title={label}
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white bg-slate-200 text-[10px] font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200",
                  isSelf && "ring-2 ring-brand-500 ring-offset-1",
                )}
              >
                {initialsForOperator(operator)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
